import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Info } from "lucide-react";
import { Controller } from "react-hook-form";
import { FileUploadInput } from "@/components/FileUploadInput";
import { Axios } from "@/utils/axios";
import { API_END_POINTS } from "@/utils/routes";
import { useUserStore } from "@/store/userStore";
import { useToast } from "@/hooks/use-toast";

const PROJECT_CATEGORIES = [
  "Web Development",
  "Mobile App Development",
  "UI/UX Design",
  "Graphic Design",
  "Content Writing",
  "Digital Marketing",
  "Data Analysis",
  "Video Editing",
  "Audio Production",
  "AI & Machine Learning",
  "Blockchain Development",
  "Other",
];

interface FileUploadInputProps {
  setIsLoading?: (isLoading: boolean) => void;
}

export const ProjectDetailsForm = ({ setIsLoading }: FileUploadInputProps) => {
  const [progress, setProgress] = useState(0);
  const { user } = useUserStore();
  const { toast } = useToast();

  const {
    register,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useFormContext();

  const uploadResumeAPI = async (file: File): Promise<any> => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("job", file);
    const resumeError = errors?.resume_url?.message;

    if (resumeError) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await Axios.post(
        `${API_END_POINTS.UPLOAD_JOB_ATTACHMENT}?userId=${user?.userId}&uploadType=job&role=enterprise`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            setProgress(percent);
          },
        }
      );
      setIsLoading(false);
      return res.data;
    } catch (error) {
      setIsLoading(false);
      toast({
        title: "Error",
        description: "Failed to upload job. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <FileUploadInput
        name="resume_url"
        buttonText="Upload Job"
        tooltipText="Upload your job (PDF/DOC, max 5MB)."
        label="Upload your job for AI-driven auto-fill of form fields"
        value={watch("resume_url")}
        onChange={(file) => setValue("resume_url", file)}
        onUpload={uploadResumeAPI}
        progress={progress}
        onUploadSuccess={(response) => {
          const enterprise = response?.job;
          if (enterprise) {
            setValue("userId", enterprise.userId || "");
            setValue("title", enterprise.title || "");
            setValue("duration", enterprise.duration || "");
            setValue("skills", enterprise.requiredSkills || "");
            setValue("projectVisibility", enterprise.projectvisibility || "public");
            setValue("description", enterprise.description || "");
            setValue("budget", enterprise.budget || "");
            setValue("experienceLevel", enterprise.experienceLevel || "");
            setValue(
              "category",
              PROJECT_CATEGORIES.includes(enterprise.category)
                ? enterprise.category
                : "Other"
            );

            toast({
              title: "Resume uploaded",
              description:
                "Resume parsed successfully and form fields updated.",
              variant: "default",
            });
          }
        }}
        toast={toast}
        error={errors.resume_url?.message as string}
      />

      <div className="space-y-2">
        <div className="flex items-center">
          <Label htmlFor="title" className="font-medium text-sm text-gray-700">
            Job Title
          </Label>
          <div className="relative group ml-2">
            <Info className="h-4 w-4 text-gray-400" />
            <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
              A clear, concise title helps attract the right talent.
            </div>
          </div>
        </div>
        <Input
          id="title"
          placeholder="Enter a clear title for your job"
          {...register("title")}
          className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
        />
        {errors.title && (
          <p className="text-xs text-red-500">
            {errors.title.message as string}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center">
          <Label
            htmlFor="description"
            className="font-medium text-sm text-gray-700"
          >
            Job Description
          </Label>
          <div className="relative group ml-2">
            <Info className="h-4 w-4 text-gray-400" />
            <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
              Detail what you need, timeline, specific requirements, and any
              other relevant information.
            </div>
          </div>
        </div>
        <Textarea
          id="description"
          placeholder="Describe your job in detail... Include job goals, specific requirements, and any other relevant information."
          {...register("description")}
          className="min-h-[150px] border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
        />
        {errors.description && (
          <p className="text-xs text-red-500">
            {errors.description.message as string}
          </p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          {useFormContext().watch("description").length}/2000 characters
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="category" className="font-medium text-sm text-gray-700">
          Job Category
        </Label>
        <Controller
          name="category"
          control={control}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger
                id="category"
                className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
              >
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_CATEGORIES.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors.category && (
          <p className="text-xs text-red-500">
            {errors.category.message as string}
          </p>
        )}
      </div>
    </div>
  );
};